import { Button } from "@components/ui/Button"
import { Modal } from "@components/ui/Modal"
import { cn } from "@lib/cn"
import { AlertTriangle } from "lucide-react"
import { useEffect, useRef, type ReactNode } from "react"

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  /** What is about to happen, in the professor's words. Name the row. */
  message: ReactNode
  confirmLabel: string
  cancelLabel: string
  closeLabel: string
  /** `danger` for anything that destroys data. Defaults to danger. */
  tone?: "danger" | "neutral"
  loading?: boolean
  /** Extra consequences worth stating before the click, e.g. "y sus 12 calificaciones". */
  consequences?: ReactNode
  /** The glyph beside the message. Defaults to the warning triangle; a
   *  reversible action names itself instead (archive → `Archive`), so it does
   *  not borrow the look of a delete. Rendered at 20px. */
  icon?: ReactNode
  /** Content under the message that is not a sentence: a warning that must
   *  read at body size, or a way to avoid the action altogether. */
  children?: ReactNode
  /** Let a long title wrap instead of truncating (see `Modal`). */
  titleWraps?: boolean
  /**
   * After the action succeeds, the SAME dialog states the outcome instead of
   * closing (PRD-73 §FR-9.3: "Le enviamos el enlace…" + «Copiar enlace»).
   * The confirm view is replaced by `body` under `title`, and one button,
   * `doneLabel`, closes through `onClose`. It is the ONLY way out: an outcome
   * shown once (a student's new link) is not dismissed by a stray tap on the
   * scrim. `doneVariant: "secondary"` when the body holds the primary move.
   * Absent = the caller closes the dialog itself, as every confirmation did
   * before.
   */
  result?: {
    title: string
    body: ReactNode
    doneLabel: string
    doneVariant?: "primary" | "secondary"
  }
}

/**
 * The one confirmation for an action that cannot be undone (PRD-59).
 *
 * A modal is normally laziness. It is the right answer here for exactly the
 * reason `notifications-toast.md` names: a confirmation has to PAUSE the
 * professor, and the alternatives this replaces did not.
 *
 * What they did instead: a kebab item that turned into "Confirmar" and
 * reverted after three seconds of inaction. Two clicks in the same 20×176px
 * strip deleted a student — a twitchy double-click on a phone, or a second
 * click after the timer silently reset, and a roster row with her grades,
 * photographs and progress records was gone with no name ever shown and no
 * statement that it was permanent. A timer is not consent.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  closeLabel,
  tone = "danger",
  loading,
  consequences,
  icon,
  children,
  titleWraps,
  result,
}: Props) {
  const resultRef = useRef<HTMLDivElement | null>(null)
  const doneRef = useRef<HTMLButtonElement | null>(null)
  const showingResult = !!result
  useEffect(() => {
    if (!showingResult) return
    // The focused Cancel left with the confirm view, which drops focus on
    // <body> — outside the dialog for a keyboard user. The caller marks what
    // should be read first with `data-autofocus` (the outcome line); else
    // the one button that closes.
    const handle = window.requestAnimationFrame(() => {
      const target =
        resultRef.current?.querySelector<HTMLElement>("[data-autofocus]") ?? doneRef.current
      target?.focus()
    })
    return () => window.cancelAnimationFrame(handle)
  }, [showingResult])

  if (result) {
    // Same component, same position: React keeps the one Modal (no second
    // entrance animation, no second focus trap), only its contents change.
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={result.title}
        titleWraps={titleWraps}
        size="sm"
        mobilePlacement="center"
        closeLabel={closeLabel}
        dismissible={false}
        footer={
          <div className="flex flex-col sm:flex-row sm:justify-end">
            <Button
              key="done"
              ref={doneRef}
              type="button"
              variant={result.doneVariant ?? "primary"}
              onClick={onClose}
            >
              {result.doneLabel}
            </Button>
          </div>
        }
      >
        <div ref={resultRef}>{result.body}</div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleWraps={titleWraps}
      size="sm"
      mobilePlacement="center"
      closeLabel={closeLabel}
      blockScrimClose={loading}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {/* `data-autofocus`: Modal focuses this first rather than the header's
              X, so the promise below is kept — Enter on the way in cancels. */}
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading} data-autofocus>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={tone === "danger" ? "destructive" : "primary"}
            onClick={onConfirm}
            loading={loading}
            // Focus lands on Cancel, not here: the safe option is the default
            // for a keyboard user who hit Enter on the way in.
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "rounded-control flex h-9 w-9 shrink-0 items-center justify-center",
            tone === "danger" ? "bg-danger-soft text-danger-ink" : "bg-inset text-ink-muted",
          )}
          aria-hidden
        >
          {icon ?? <AlertTriangle className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-ink text-sm">{message}</p>
          {consequences ? <p className="text-meta">{consequences}</p> : null}
          {children ? <div className="space-y-2 pt-2">{children}</div> : null}
        </div>
      </div>
    </Modal>
  )
}
